import { isString, isEmpty, isUndefined } from 'lodash';
import { logger, stats } from '@fiverr-private/obs';
import { send } from '../bigQuery/emitter';
import { MONICORE } from '../constants';
import { ContextNotFoundError } from '../errors';
import { getContext, storeStartedSpan, getSpanStart, isInSamplingPercentage } from '../platform/context';
import { performance } from '../platform/performance';
import { EVENT_TYPES, ERROR_CODES, ENTRY_TYPES } from './constants';

/**
 * Returns the reference time used for performance measurement.
 * In nodejs, it returns the timestamp at which the current node process began
 * In the browser, it returns the timestamp at which performance measurements starts.
 * @returns {number}
 */
const getTimeOrigin = () => {
    let timeOrigin = performance.timeOrigin;

    // Support for legacy browsers
    if (typeof timeOrigin !== 'number') {
        timeOrigin = performance.timing && performance.timing.navigationStart;
    }
    return timeOrigin;
};

/**
 * Returns the now time is ms calculated by performance component.
 * @returns {number}
 */
export const now = () => performance.now() + getTimeOrigin();

/**
 * Validate that the value used in an event is valid
 * Only undefined or strings are accepted.
 * @param {string} value
 * @returns {boolean} - true if the value is valid
 */
const validateValue = (value) => {
    if (isUndefined(value) || isString(value)) {
        return true;
    }

    logger.error(`Expected value to be a string. Instead got a ${typeof value}`, {
        caughtBy: MONICORE,
        value,
    });

    return false;
};

/**
 * Validate that the name used in an event is valid
 * Only non empty strings are accepted.
 * @param {string} value
 * @returns {boolean} - true if the value is valid
 */
const validateName = (name) => {
    if (isString(name) && !isEmpty(name)) {
        return true;
    }

    logger.error(`Expected name to be a string. Instead got a ${typeof name}`, {
        caughtBy: MONICORE,
        name,
    });

    return false;
};

const handleContextNotFoundError = () => {
    // Don't log in this error, since all services using layout,
    // but with outdated perseus will receive it
    stats.count('monicore', 'context_not_found');
};

/**
 * Check if sampling was applied to the event
 * and sent it to the BigQuery emitter accordingly.
 * @param {import('../bigQuery/emitter').Event} event
 * @returns {void}
 */
const sendIfNeeded = (event) => isInSamplingPercentage() && send(event);

/**
 * Start a new measurement span.
 * @param {string} name
 * @returns {void}
 */
export const startSpan = (name) => {
    if (!validateName(name)) {
        return;
    }

    try {
        storeStartedSpan(name, now());
    } catch (e) {
        if (e instanceof ContextNotFoundError) {
            handleContextNotFoundError();
        } else {
            logger.warn(e, {
                caughtBy: MONICORE,
                code: ERROR_CODES.START_SPAN,
            });
        }
    }
};

/**
 * Close a measurement span.
 * A measurement event will be sent to bigQuery,
 * including context and span duration
 * @param {string} name
 * @param {string} value - Optional value
 * @returns {void}
 */
export const endSpan = (name, value) => {
    if (!validateName(name)) {
        return;
    }

    if (!validateValue(value)) {
        value = undefined;
    }

    try {
        const endedAt = now();
        const startedAt = getSpanStart(name);

        if (!startedAt) {
            throw new Error(`Span ${name} was ended without beeing previously started`);
        }

        const duration = endedAt - startedAt;

        const event = {
            name,
            duration,
            value,
            type: ENTRY_TYPES.MEASURE,
            createdAt: startedAt,
            context: getContext(),
        };

        sendIfNeeded(event);
    } catch (e) {
        if (e instanceof ContextNotFoundError) {
            handleContextNotFoundError();
        } else {
            logger.warn(e, {
                caughtBy: MONICORE,
                code: ERROR_CODES.END_SPAN,
            });
        }
    }
};

/**
 * Add an event
 * The event will be sent to bigQuery,
 * including its context
 * @param {string} name
 * @param {string} value - Optional value
 * @returns {void}
 */
export const addEvent = (name, value = undefined, type = ENTRY_TYPES.EVENT) => {
    if (!validateName(name)) {
        return;
    }

    if (!EVENT_TYPES.includes(type)) {
        logger.error(`Expected "type" to be event or error. Instead got a ${type}`, {
            caughtBy: MONICORE,
            code: ERROR_CODES.ADD_EVENT,
        });
        return;
    }

    if (!validateValue(value)) {
        value = undefined;
    }

    try {
        const event = {
            name,
            value,
            type,
            createdAt: now(),
            context: getContext(),
        };

        sendIfNeeded(event);
    } catch (e) {
        if (e instanceof ContextNotFoundError) {
            handleContextNotFoundError();
        } else {
            logger.warn(e, {
                caughtBy: MONICORE,
                code: ERROR_CODES.ADD_EVENT,
            });
        }
    }
};

/**
 * Add an error
 * The error will be sent to bigQuery,
 * including its context
 * @param {string} name
 * @param {string} errorCode - Optional errorCode
 * @returns {void}
 */
export const addError = (name, errorCode) => {
    try {
        return addEvent(name, errorCode, ENTRY_TYPES.ERROR);
    } catch (e) {
        logger.warn(e, {
            caughtBy: MONICORE,
            code: ERROR_CODES.ADD_ERROR,
        });
    }
};
